import React, { useEffect, useState } from 'react';
import { FilterIcon, XIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleQuantile } from 'd3-scale';
const EnrollmentMap = () => {
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [stateData, setStateData] = useState<Record<string, any>>({});
  const [stateDetails, setStateDetails] = useState<Record<string, any>>({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [calculationMethod, setCalculationMethod] = useState('lives');
  const [mapView, setMapView] = useState('coverage');
  const [expandedInsight, setExpandedInsight] = useState<number | null>(null);
  // Sample regional insights
  const regionalInsights = [{
    id: 1,
    region: 'Northeast',
    coverageScore: 32,
    status: 'negative',
    dominantPayers: ['Aetna', 'BCBS Massachusetts', 'Cigna'],
    totalLives: '42.3M',
    summary: "The Northeast region shows predominantly negative coverage policies, with 32.1M lives under negative coverage. Aetna's recent policy change has significantly impacted this region, particularly in New York and Massachusetts.",
    keyStates: [{
      name: 'New York',
      stateCode: 'NY',
      status: 'negative',
      lives: '12.4M',
      dominantPayer: 'Aetna'
    }, {
      name: 'Massachusetts',
      stateCode: 'MA',
      status: 'negative',
      lives: '8.2M',
      dominantPayer: 'BCBS Massachusetts'
    }, {
      name: 'Connecticut',
      stateCode: 'CT',
      status: 'mixed',
      lives: '5.1M',
      dominantPayer: 'Cigna'
    }],
    recommendations: ['Focus advocacy efforts on BCBS Massachusetts due to regional influence', 'Develop targeted case studies for New York physicians', 'Monitor upcoming policy reviews from Cigna (Q3 2025)']
  }, {
    id: 2,
    region: 'Southeast',
    coverageScore: 58,
    status: 'mixed',
    dominantPayers: ['BCBS Alabama', 'Humana', 'BCBS Florida'],
    totalLives: '38.7M',
    summary: 'The Southeast shows mixed coverage with a positive trend in Alabama and Georgia. Recent BCBS Alabama policy change provides a template for advocacy with other BCBS entities in the region.',
    keyStates: [{
      name: 'Florida',
      stateCode: 'FL',
      status: 'negative',
      lives: '11.8M',
      dominantPayer: 'BCBS Florida'
    }, {
      name: 'Georgia',
      stateCode: 'GA',
      status: 'positive',
      lives: '7.2M',
      dominantPayer: 'Humana'
    }, {
      name: 'Alabama',
      stateCode: 'AL',
      status: 'positive',
      lives: '2.5M',
      dominantPayer: 'BCBS Alabama'
    }],
    recommendations: ['Leverage BCBS Alabama policy language for advocacy with BCBS Florida', 'Expand physician education program in Georgia', 'Target upcoming policy reviews in North Carolina and Tennessee']
  }, {
    id: 3,
    region: 'Midwest',
    coverageScore: 45,
    status: 'mixed',
    dominantPayers: ['BCBS Illinois', 'UnitedHealthcare', 'Anthem'],
    totalLives: '52.1M',
    summary: "The Midwest region has varying coverage policies with negative trends in Illinois and Ohio, but positive developments in Minnesota. UnitedHealthcare's upcoming policy review represents a significant opportunity.",
    keyStates: [{
      name: 'Illinois',
      stateCode: 'IL',
      status: 'negative',
      lives: '9.3M',
      dominantPayer: 'BCBS Illinois'
    }, {
      name: 'Ohio',
      stateCode: 'OH',
      status: 'negative',
      lives: '8.7M',
      dominantPayer: 'Anthem'
    }, {
      name: 'Minnesota',
      stateCode: 'MN',
      status: 'positive',
      lives: '4.2M',
      dominantPayer: 'UnitedHealthcare'
    }],
    recommendations: ['Prepare comprehensive evidence package for UnitedHealthcare review', 'Focus KOL development in Minnesota to influence regional opinion', 'Monitor Anthem policy developments in Ohio']
  }];
  // Get state name from state code
  const getStateName = stateCode => {
    const stateNames = {
      AL: 'Alabama',
      AK: 'Alaska',
      AZ: 'Arizona',
      AR: 'Arkansas',
      CA: 'California',
      CO: 'Colorado',
      CT: 'Connecticut',
      DE: 'Delaware',
      FL: 'Florida',
      GA: 'Georgia',
      HI: 'Hawaii',
      ID: 'Idaho',
      IL: 'Illinois',
      IN: 'Indiana',
      IA: 'Iowa',
      KS: 'Kansas',
      KY: 'Kentucky',
      LA: 'Louisiana',
      ME: 'Maine',
      MD: 'Maryland',
      MA: 'Massachusetts',
      MI: 'Michigan',
      MN: 'Minnesota',
      MS: 'Mississippi',
      MO: 'Missouri',
      MT: 'Montana',
      NE: 'Nebraska',
      NV: 'Nevada',
      NH: 'New Hampshire',
      NJ: 'New Jersey',
      NM: 'New Mexico',
      NY: 'New York',
      NC: 'North Carolina',
      ND: 'North Dakota',
      OH: 'Ohio',
      OK: 'Oklahoma',
      OR: 'Oregon',
      PA: 'Pennsylvania',
      RI: 'Rhode Island',
      SC: 'South Carolina',
      SD: 'South Dakota',
      TN: 'Tennessee',
      TX: 'Texas',
      UT: 'Utah',
      VT: 'Vermont',
      VA: 'Virginia',
      WA: 'Washington',
      WV: 'West Virginia',
      WI: 'Wisconsin',
      WY: 'Wyoming'
    };
    return stateNames[stateCode] || stateCode;
  };
  // Initialize state data
  useEffect(() => {
    // Basic data for all states
    const initialStateData = {
      AL: {
        status: 'positive',
        impact: 28,
        change: false
      },
      AK: {
        status: 'negative',
        impact: 65,
        change: false
      },
      AZ: {
        status: 'negative',
        impact: 72,
        change: true
      },
      AR: {
        status: 'no-policy',
        impact: 45,
        change: false
      },
      CA: {
        status: 'negative',
        impact: 85,
        change: true
      },
      CO: {
        status: 'negative',
        impact: 62,
        change: true
      },
      CT: {
        status: 'mixed',
        impact: 55,
        change: false
      },
      DE: {
        status: 'negative',
        impact: 58,
        change: false
      },
      FL: {
        status: 'negative',
        impact: 78,
        change: true
      },
      GA: {
        status: 'positive',
        impact: 35,
        change: false
      },
      HI: {
        status: 'no-policy',
        impact: 30,
        change: false
      },
      ID: {
        status: 'negative',
        impact: 52,
        change: false
      },
      IL: {
        status: 'negative',
        impact: 75,
        change: true
      },
      IN: {
        status: 'negative',
        impact: 68,
        change: false
      },
      IA: {
        status: 'mixed',
        impact: 48,
        change: false
      },
      KS: {
        status: 'negative',
        impact: 60,
        change: false
      },
      KY: {
        status: 'negative',
        impact: 62,
        change: true
      },
      LA: {
        status: 'negative',
        impact: 70,
        change: false
      },
      ME: {
        status: 'negative',
        impact: 55,
        change: false
      },
      MD: {
        status: 'negative',
        impact: 58,
        change: true
      },
      MA: {
        status: 'negative',
        impact: 65,
        change: false
      },
      MI: {
        status: 'negative',
        impact: 72,
        change: true
      },
      MN: {
        status: 'positive',
        impact: 32,
        change: false
      },
      MS: {
        status: 'negative',
        impact: 68,
        change: false
      },
      MO: {
        status: 'negative',
        impact: 65,
        change: true
      },
      MT: {
        status: 'no-policy',
        impact: 40,
        change: false
      },
      NE: {
        status: 'negative',
        impact: 58,
        change: false
      },
      NV: {
        status: 'negative',
        impact: 62,
        change: true
      },
      NH: {
        status: 'negative',
        impact: 55,
        change: false
      },
      NJ: {
        status: 'negative',
        impact: 68,
        change: false
      },
      NM: {
        status: 'mixed',
        impact: 50,
        change: false
      },
      NY: {
        status: 'negative',
        impact: 78,
        change: true
      },
      NC: {
        status: 'mixed',
        impact: 52,
        change: true
      },
      ND: {
        status: 'no-policy',
        impact: 38,
        change: false
      },
      OH: {
        status: 'negative',
        impact: 70,
        change: false
      },
      OK: {
        status: 'negative',
        impact: 65,
        change: false
      },
      OR: {
        status: 'negative',
        impact: 60,
        change: true
      },
      PA: {
        status: 'negative',
        impact: 72,
        change: false
      },
      RI: {
        status: 'negative',
        impact: 58,
        change: false
      },
      SC: {
        status: 'mixed',
        impact: 52,
        change: true
      },
      SD: {
        status: 'no-policy',
        impact: 35,
        change: false
      },
      TN: {
        status: 'negative',
        impact: 68,
        change: false
      },
      TX: {
        status: 'negative',
        impact: 82,
        change: true
      },
      UT: {
        status: 'negative',
        impact: 60,
        change: false
      },
      VT: {
        status: 'negative',
        impact: 55,
        change: false
      },
      VA: {
        status: 'mixed',
        impact: 50,
        change: true
      },
      WA: {
        status: 'negative',
        impact: 65,
        change: false
      },
      WV: {
        status: 'negative',
        impact: 62,
        change: false
      },
      WI: {
        status: 'mixed',
        impact: 48,
        change: true
      },
      WY: {
        status: 'no-policy',
        impact: 35,
        change: false
      }
    };
    // Detailed data for selected states
    const stateDetailsData = {
      TX: {
        name: 'Texas',
        population: '29.9M',
        insuredLives: '18.6M',
        summary: 'Texas shows predominantly negative coverage for peripheral nerve stimulation, with 15.1M lives (81.2%) under negative policies. The state is dominated by Health Care Service Corporation and UnitedHealthcare, both with negative policies. Recent policy changes from Aetna further restricted coverage. However, Novitas Solutions and Humana maintain positive coverage policies, creating potential opportunities for targeted market access.',
        payerData: [{
          payer: 'Health Care Service Corporation',
          status: 'negative',
          enrollment: '6,600,309',
          priorAuth: 'Yes',
          changeDate: '03/15/2025'
        }, {
          payer: 'United Healthcare',
          status: 'negative',
          enrollment: '3,592,109',
          priorAuth: 'Yes',
          changeDate: '02/01/2025'
        }, {
          payer: 'Novitas Solutions',
          status: 'positive',
          enrollment: '1,890,214',
          priorAuth: 'No',
          changeDate: '11/15/2024'
        }, {
          payer: 'Centene',
          status: 'negative',
          enrollment: '1,792,681',
          priorAuth: 'Yes',
          changeDate: '01/10/2025'
        }, {
          payer: 'Cigna',
          status: 'negative',
          enrollment: '1,518,703',
          priorAuth: 'Yes',
          changeDate: '12/05/2024'
        }, {
          payer: 'Aetna',
          status: 'negative',
          enrollment: '1,462,686',
          priorAuth: 'Yes',
          changeDate: '04/14/2025'
        }, {
          payer: 'Humana',
          status: 'positive',
          enrollment: '1,208,322',
          priorAuth: 'No',
          changeDate: '09/22/2024'
        }, {
          payer: 'BCBS Anthem',
          status: 'negative',
          enrollment: '1,109,645',
          priorAuth: 'Yes',
          changeDate: '03/01/2025'
        }, {
          payer: 'Baylor Scott & White Health Plan',
          status: 'no-policy',
          enrollment: '295,345',
          priorAuth: 'N/A',
          changeDate: 'N/A'
        }, {
          payer: 'BCBS Alabama',
          status: 'positive',
          enrollment: '73,946',
          priorAuth: 'No',
          changeDate: '10/01/2024'
        }, {
          payer: 'BCBS Tennessee',
          status: 'negative',
          enrollment: '47,136',
          priorAuth: 'Yes',
          changeDate: '05/15/2025'
        }],
        recommendations: {
          opportunity: "Leverage Novitas Solutions' positive policy to advocate with other Medicare contractors in the region. Focus on building clinical evidence with Novitas-covered physicians.",
          challenge: "Health Care Service Corporation's dominance in the market represents a significant barrier. Their policy explicitly lists Freedom PNS as experimental/investigational.",
          actionItems: ['Focus physician education efforts on Humana and Novitas networks', 'Prepare for HCSC policy review scheduled for Q3 2025', 'Develop targeted clinical evidence package for UnitedHealthcare', 'Monitor Baylor Scott & White for policy development opportunity']
        }
      },
      CA: {
        name: 'California',
        population: '39.5M',
        insuredLives: '26.3M',
        summary: 'California has predominantly negative coverage for peripheral nerve stimulation, with 22.4M lives (85.2%) under restrictive policies. Kaiser Permanente and Blue Shield of California dominate the market with negative policies. Recent legislation (SB-1033) may create opportunities for policy reconsideration in 2025.',
        payerData: [{
          payer: 'Kaiser Permanente',
          status: 'negative',
          enrollment: '9,127,355',
          priorAuth: 'Yes',
          changeDate: '05/10/2025'
        }, {
          payer: 'Blue Shield of California',
          status: 'negative',
          enrollment: '4,562,109',
          priorAuth: 'Yes',
          changeDate: '01/15/2025'
        }, {
          payer: 'Anthem Blue Cross',
          status: 'negative',
          enrollment: '3,890,214',
          priorAuth: 'Yes',
          changeDate: '02/28/2025'
        }, {
          payer: 'Health Net',
          status: 'negative',
          enrollment: '2,792,681',
          priorAuth: 'Yes',
          changeDate: '03/22/2025'
        }, {
          payer: 'Centene',
          status: 'negative',
          enrollment: '2,118,703',
          priorAuth: 'Yes',
          changeDate: '11/05/2024'
        }, {
          payer: 'Noridian Healthcare Solutions',
          status: 'positive',
          enrollment: '1,962,686',
          priorAuth: 'No',
          changeDate: '10/14/2024'
        }, {
          payer: 'UnitedHealthcare',
          status: 'negative',
          enrollment: '1,608,322',
          priorAuth: 'Yes',
          changeDate: '04/01/2025'
        }],
        recommendations: {
          opportunity: "Leverage Noridian's positive Medicare policy to build clinical evidence with key physicians in academic medical centers.",
          challenge: "Kaiser Permanente's closed system and dominant market position creates significant barriers to access.",
          actionItems: ['Engage with California Medical Association on evidence reviews', 'Monitor implementation of SB-1033 and its impact on medical policy reviews', 'Develop KOL relationships within Noridian coverage networks', 'Prepare for Kaiser Permanente policy review in Q2 2025']
        }
      },
      NY: {
        name: 'New York',
        population: '19.8M',
        insuredLives: '15.2M',
        summary: 'New York shows predominantly negative coverage for peripheral nerve stimulation, with 11.9M lives (78.3%) under negative policies. The market is led by Empire BlueCross BlueShield and UnitedHealthcare. Recent state legislation on pain management alternatives may create policy review opportunities in 2025.',
        payerData: [{
          payer: 'Empire BlueCross BlueShield',
          status: 'negative',
          enrollment: '4,827,355',
          priorAuth: 'Yes',
          changeDate: '02/15/2025'
        }, {
          payer: 'UnitedHealthcare',
          status: 'negative',
          enrollment: '3,162,109',
          priorAuth: 'Yes',
          changeDate: '03/10/2025'
        }, {
          payer: 'Aetna',
          status: 'negative',
          enrollment: '2,390,214',
          priorAuth: 'Yes',
          changeDate: '01/20/2025'
        }, {
          payer: 'Fidelis Care',
          status: 'negative',
          enrollment: '1,792,681',
          priorAuth: 'Yes',
          changeDate: '04/05/2025'
        }, {
          payer: 'Cigna',
          status: 'negative',
          enrollment: '1,218,703',
          priorAuth: 'Yes',
          changeDate: '12/10/2024'
        }, {
          payer: 'National Government Services',
          status: 'mixed',
          enrollment: '1,062,686',
          priorAuth: 'Varies',
          changeDate: '09/30/2024'
        }, {
          payer: 'Healthfirst',
          status: 'no-policy',
          enrollment: '708,322',
          priorAuth: 'N/A',
          changeDate: 'N/A'
        }],
        recommendations: {
          opportunity: 'Engage with New York State Pain Management Steering Committee to influence future policy development.',
          challenge: "Empire BCBS and UnitedHealthcare's restrictive policies affect over 50% of the commercially insured population.",
          actionItems: ['Develop relationships with key physicians at NYU and Columbia medical centers', 'Monitor implementation of the NY Pain Management Alternative Therapies Act', "Prepare evidence package for National Government Services' upcoming review", 'Target Healthfirst for policy development opportunity']
        }
      },
      FL: {
        name: 'Florida',
        population: '21.5M',
        insuredLives: '14.8M',
        summary: 'Florida has predominantly negative coverage for peripheral nerve stimulation, with 11.5M lives (77.7%) under restrictive policies. Florida Blue and UnitedHealthcare dominate the market with negative policies. Recent increase in Medicare Advantage enrollment presents opportunities with select payers.',
        payerData: [{
          payer: 'Florida Blue (BCBS)',
          status: 'negative',
          enrollment: '5,127,355',
          priorAuth: 'Yes',
          changeDate: '03/15/2025'
        }, {
          payer: 'UnitedHealthcare',
          status: 'negative',
          enrollment: '2,862,109',
          priorAuth: 'Yes',
          changeDate: '02/20/2025'
        }, {
          payer: 'Humana',
          status: 'mixed',
          enrollment: '2,190,214',
          priorAuth: 'Varies',
          changeDate: '11/10/2024'
        }, {
          payer: 'Aetna',
          status: 'negative',
          enrollment: '1,592,681',
          priorAuth: 'Yes',
          changeDate: '01/05/2025'
        }, {
          payer: 'Cigna',
          status: 'negative',
          enrollment: '1,118,703',
          priorAuth: 'Yes',
          changeDate: '12/15/2024'
        }, {
          payer: 'First Coast Service Options',
          status: 'negative',
          enrollment: '1,062,686',
          priorAuth: 'Yes',
          changeDate: '10/01/2024'
        }, {
          payer: 'AvMed',
          status: 'no-policy',
          enrollment: '508,322',
          priorAuth: 'N/A',
          changeDate: 'N/A'
        }],
        recommendations: {
          opportunity: "Target Humana's mixed policy for improvement, as they have significant Medicare Advantage market share.",
          challenge: "Florida Blue's dominant position and explicit negative policy creates market access barriers.",
          actionItems: ['Develop relationships with key pain management specialists in Miami and Orlando', 'Prepare for Florida Blue policy review scheduled for Q1 2025', 'Engage with Humana medical directors on their mixed policy', 'Monitor First Coast Service Options for Medicare policy review opportunities']
        }
      }
    };
    // Add additional details to states from regional insights
    regionalInsights.forEach(region => {
      region.keyStates.forEach(state => {
        if (initialStateData[state.stateCode]) {
          initialStateData[state.stateCode].region = region.region;
          initialStateData[state.stateCode].lives = state.lives;
          initialStateData[state.stateCode].dominantPayer = state.dominantPayer;
        }
      });
    });
    setStateData(initialStateData);
    setStateDetails(stateDetailsData);
  }, []);
  const openStateModal = (stateCode: string) => {
    setSelectedState(stateCode);
  };
  const closeStateModal = () => {
    setSelectedState(null);
  };
  const toggleInsight = (id: number) => {
    if (expandedInsight === id) {
      setExpandedInsight(null);
    } else {
      setExpandedInsight(id);
    }
  };
  // Get color based on state data and current view
  const getStateColor = geo => {
    const stateCode = geo.id;
    const state = stateData[stateCode];
    if (!state) return '#EEE';
    if (mapView === 'coverage') {
      if (activeFilter !== 'all' && state.status !== activeFilter) {
        return '#E5E7EB'; // Gray out filtered states
      }
      switch (state.status) {
        case 'positive':
          return '#22c55e';
        // Green
        case 'negative':
          return '#ef4444';
        // Red
        case 'no-policy':
          return '#eab308';
        // Yellow
        case 'mixed':
          return '#f59e0b';
        // Amber
        default:
          return '#E5E7EB';
      }
    } else if (mapView === 'impact') {
      // Create a scale for impact values
      const colorScale = scaleQuantile().domain(Object.values(stateData).map(d => d.impact)).range(['#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444' // Darkest red
      ]);
      return colorScale(state.impact);
    } else if (mapView === 'change') {
      if (activeFilter === 'changes' && !state.change) {
        return '#E5E7EB';
      }
      return state.change ? '#a855f7' : '#E5E7EB'; // Purple for changes
    }
    return '#E5E7EB';
  };
  // US TopoJSON with states
  const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-763">
      <div className="mb-6" data-id="element-764">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-765">
          <div data-id="element-766">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-767">
              Coverage Intelligence Map
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-768">
              Geographic analysis of policy coverage and market impact
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-769">
            <select className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-770">
              <option data-id="element-771">Freedom® PNS System</option>
              <option data-id="element-772">All Products</option>
            </select>
            <button className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md shadow-sm hover:bg-blue-700" data-id="element-773">
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Map View Controls */}
      <div className="mb-6 flex flex-wrap gap-4" data-id="element-774">
        <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-775">
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${mapView === 'coverage' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setMapView('coverage')} data-id="element-776">
            Coverage Status
          </button>
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${mapView === 'impact' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setMapView('impact')} data-id="element-777">
            Market Impact
          </button>
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${mapView === 'change' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setMapView('change')} data-id="element-778">
            Recent Changes
          </button>
        </div>
        <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-779">
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${calculationMethod === 'lives' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setCalculationMethod('lives')} data-id="element-780">
            By Lives
          </button>
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${calculationMethod === 'policies' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setCalculationMethod('policies')} data-id="element-781">
            By Policies
          </button>
          <button className={`px-3 py-1.5 text-sm font-medium rounded ${calculationMethod === 'revenue' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => setCalculationMethod('revenue')} data-id="element-782">
            By Revenue
          </button>
        </div>
        <div className="ml-auto" data-id="element-783">
          <button className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" data-id="element-784">
            <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-785" />
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="mb-6 flex flex-wrap gap-2" data-id="element-786">
        <button className={`px-3 py-1.5 text-sm font-medium rounded-full ${activeFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setActiveFilter('all')} data-id="element-787">
          All Coverage
        </button>
        <button className={`px-3 py-1.5 text-sm font-medium rounded-full ${activeFilter === 'positive' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setActiveFilter('positive')} data-id="element-788">
          Positive Coverage
        </button>
        <button className={`px-3 py-1.5 text-sm font-medium rounded-full ${activeFilter === 'negative' ? 'bg-red-100 text-red-700' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setActiveFilter('negative')} data-id="element-789">
          Negative Coverage
        </button>
        <button className={`px-3 py-1.5 text-sm font-medium rounded-full ${activeFilter === 'no-policy' ? 'bg-yellow-100 text-yellow-700' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setActiveFilter('no-policy')} data-id="element-790">
          No Policy
        </button>
        <button className={`px-3 py-1.5 text-sm font-medium rounded-full ${activeFilter === 'changes' ? 'bg-purple-100 text-purple-700' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setActiveFilter('changes')} data-id="element-791">
          Recent Changes
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-792">
        {/* Map visualization */}
        <div className="lg:col-span-2" data-id="element-793">
          <div className="bg-white rounded-lg shadow" data-id="element-794">
            <div className="p-4 border-b border-gray-200" data-id="element-795">
              <div className="flex items-center justify-between" data-id="element-796">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-797">
                  {mapView === 'coverage' && 'Coverage Status by State'}
                  {mapView === 'impact' && 'Market Impact by State'}
                  {mapView === 'change' && 'Recent Policy Changes by State'}
                </h3>
                <div className="flex items-center space-x-2" data-id="element-798">
                  <select className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-799">
                    <option data-id="element-800">All Payers</option>
                    <option data-id="element-801">Commercial Only</option>
                    <option data-id="element-802">Medicare Only</option>
                    <option data-id="element-803">Medicaid Only</option>
                  </select>
                  <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-804">
                    <DownloadIcon className="w-5 h-5" data-id="element-805" />
                  </button>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500" data-id="element-806">
                {mapView === 'coverage' && 'Visualization of coverage status weighted by insured lives'}
                {mapView === 'impact' && 'Visualization of market impact based on revenue potential'}
                {mapView === 'change' && 'States with policy changes in the last 90 days'}
              </p>
            </div>
            {/* Interactive US Map using react-simple-maps */}
            <div className="p-6" data-id="element-807">
              <div className="relative bg-white border border-gray-200 rounded-lg" data-id="element-808">
                <ComposableMap projection="geoAlbersUsa" projectionConfig={{
                scale: 1000
              }} width={980} height={500} style={{
                width: '100%',
                height: 'auto'
              }} data-id="element-809">
                  <Geographies geography={geoUrl} data-id="element-810">
                    {({
                    geographies
                  }) => geographies.map(geo => {
                    const stateCode = geo.id;
                    const isHighlighted = Object.keys(stateDetails).includes(stateCode);
                    return <Geography key={geo.rsmKey} geography={geo} onClick={() => openStateModal(stateCode)} style={{
                      default: {
                        fill: getStateColor(geo),
                        stroke: '#FFF',
                        strokeWidth: 0.5,
                        outline: 'none'
                      },
                      hover: {
                        fill: isHighlighted ? '#3b82f6' : '#a3bffa',
                        stroke: '#FFF',
                        strokeWidth: 1,
                        outline: 'none',
                        cursor: 'pointer'
                      },
                      pressed: {
                        fill: '#3b82f6',
                        stroke: '#FFF',
                        strokeWidth: 1,
                        outline: 'none'
                      }
                    }} data-id="element-811" />;
                  })}
                  </Geographies>
                </ComposableMap>
                <div className="absolute bottom-4 left-4" data-id="element-812">
                  <div className="p-3 bg-white rounded-lg shadow" data-id="element-813">
                    {mapView === 'coverage' && <>
                        <h4 className="mb-2 text-xs font-medium text-gray-700" data-id="element-814">
                          Coverage Status
                        </h4>
                        <div className="space-y-2" data-id="element-815">
                          <div className="flex items-center" data-id="element-816">
                            <div className="w-4 h-4 bg-green-500 rounded-sm mr-2" data-id="element-817"></div>
                            <span className="text-xs text-gray-600" data-id="element-818">
                              Positive Coverage
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-819">
                            <div className="w-4 h-4 bg-red-500 rounded-sm mr-2" data-id="element-820"></div>
                            <span className="text-xs text-gray-600" data-id="element-821">
                              Negative Coverage
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-822">
                            <div className="w-4 h-4 bg-yellow-500 rounded-sm mr-2" data-id="element-823"></div>
                            <span className="text-xs text-gray-600" data-id="element-824">
                              No Policy
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-825">
                            <div className="w-4 h-4 bg-amber-500 rounded-sm mr-2" data-id="element-826"></div>
                            <span className="text-xs text-gray-600" data-id="element-827">
                              Mixed Coverage
                            </span>
                          </div>
                        </div>
                      </>}
                    {mapView === 'impact' && <>
                        <h4 className="mb-2 text-xs font-medium text-gray-700" data-id="element-828">
                          Market Impact
                        </h4>
                        <div className="flex items-center space-x-1" data-id="element-829">
                          <div className="w-4 h-4 bg-red-500 rounded-sm" data-id="element-830"></div>
                          <div className="w-4 h-4 bg-red-400 rounded-sm" data-id="element-831"></div>
                          <div className="w-4 h-4 bg-red-300 rounded-sm" data-id="element-832"></div>
                          <div className="w-4 h-4 bg-red-200 rounded-sm" data-id="element-833"></div>
                          <div className="w-4 h-4 bg-red-100 rounded-sm" data-id="element-834"></div>
                        </div>
                        <div className="flex items-center justify-between mt-1 text-xs text-gray-600" data-id="element-835">
                          <span data-id="element-836">High Impact</span>
                          <span data-id="element-837">Low Impact</span>
                        </div>
                      </>}
                    {mapView === 'change' && <>
                        <h4 className="mb-2 text-xs font-medium text-gray-700" data-id="element-838">
                          Recent Changes
                        </h4>
                        <div className="flex items-center" data-id="element-839">
                          <div className="w-4 h-4 bg-purple-500 rounded-sm mr-2" data-id="element-840"></div>
                          <span className="text-xs text-gray-600" data-id="element-841">
                            Policy Changed (Last 90 Days)
                          </span>
                        </div>
                        <div className="flex items-center mt-1" data-id="element-842">
                          <div className="w-4 h-4 bg-gray-300 rounded-sm mr-2" data-id="element-843"></div>
                          <span className="text-xs text-gray-600" data-id="element-844">
                            No Recent Changes
                          </span>
                        </div>
                      </>}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center" data-id="element-845">
                <div className="text-sm text-gray-500" data-id="element-846">
                  Click on a state to view detailed coverage information
                </div>
                <div className="text-sm text-gray-700 font-medium" data-id="element-847">
                  {mapView === 'coverage' && 'Coverage analysis based on state-level policy data'}
                  {mapView === 'impact' && 'Top 5 impact states: TX, NY, FL, CA, IL'}
                  {mapView === 'change' && '17 states with policy changes in the last 90 days'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Regional Insights */}
        <div data-id="element-848">
          <div className="bg-white rounded-lg shadow" data-id="element-849">
            <div className="p-4 border-b border-gray-200" data-id="element-850">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-851">
                Regional Insights
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-852">
                Analysis of coverage patterns by geographic region
              </p>
            </div>
            <div className="p-4" data-id="element-853">
              <div className="space-y-4" data-id="element-854">
                {regionalInsights.map(insight => <div key={insight.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-855">
                    <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggleInsight(insight.id)} data-id="element-856">
                      <div className="flex justify-between items-center" data-id="element-857">
                        <h4 className="text-base font-medium text-gray-900" data-id="element-858">
                          {insight.region}
                        </h4>
                        <div className="flex items-center space-x-2" data-id="element-859">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${insight.status === 'negative' ? 'bg-red-100 text-red-800' : insight.status === 'positive' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-860">
                            {insight.status === 'mixed' ? 'Mixed Coverage' : insight.status === 'positive' ? 'Positive Trend' : 'Negative Trend'}
                          </span>
                          {expandedInsight === insight.id ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-861" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-862" />}
                        </div>
                      </div>
                      <div className="mt-2 flex justify-between items-center" data-id="element-863">
                        <div className="text-sm text-gray-500" data-id="element-864">
                          <span className="font-medium" data-id="element-865">
                            {insight.totalLives}
                          </span>{' '}
                          total lives
                        </div>
                        <div className="flex items-center" data-id="element-866">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-867">
                            <div className={`h-full ${insight.status === 'negative' ? 'bg-red-500' : insight.status === 'positive' ? 'bg-green-500' : 'bg-yellow-500'}`} style={{
                          width: `${insight.coverageScore}%`
                        }} data-id="element-868"></div>
                          </div>
                          <span className="ml-2 text-xs text-gray-500" data-id="element-869">
                            {insight.coverageScore}%
                          </span>
                        </div>
                      </div>
                    </div>
                    {expandedInsight === insight.id && <div className="px-4 py-3 border-t border-gray-200 bg-gray-50" data-id="element-870">
                        <p className="text-sm text-gray-700 mb-4" data-id="element-871">
                          {insight.summary}
                        </p>
                        <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-872">
                          Key States
                        </h5>
                        <div className="overflow-x-auto" data-id="element-873">
                          <table className="min-w-full divide-y divide-gray-200 mb-4" data-id="element-874">
                            <thead className="bg-gray-100" data-id="element-875">
                              <tr data-id="element-876">
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-877">
                                  State
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-878">
                                  Status
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-879">
                                  Lives
                                </th>
                                <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-880">
                                  Dominant Payer
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200" data-id="element-881">
                              {insight.keyStates.map((state, idx) => <tr key={idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => openStateModal(state.stateCode)} data-id="element-882">
                                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-883">
                                    {state.name}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap" data-id="element-884">
                                    <StatusBadge status={state.status as any} showText={false} size="sm" data-id="element-885" />
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-886">
                                    {state.lives}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-887">
                                    {state.dominantPayer}
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-888">
                          Strategic Recommendations
                        </h5>
                        <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1 mb-3" data-id="element-889">
                          {insight.recommendations.map((rec, idx) => <li key={idx} data-id="element-890">{rec}</li>)}
                        </ul>
                        <div className="flex justify-end" data-id="element-891">
                          <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-892">
                            View Full Regional Analysis →
                          </button>
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* State Modal */}
      {selectedState && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-id="element-893">
          <div className="w-full max-w-4xl p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-auto" data-id="element-894">
            <div className="flex items-center justify-between mb-4" data-id="element-895">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-896">
                {getStateName(selectedState)} Coverage Analysis
              </h2>
              <button onClick={closeStateModal} data-id="element-897">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-898" />
              </button>
            </div>
            {stateDetails[selectedState] ?
        // Detailed state data available
        <>
                <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3" data-id="element-899">
                  <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-900">
                    <p className="text-sm text-gray-500" data-id="element-901">Population</p>
                    <p className="text-xl font-semibold" data-id="element-902">
                      {stateDetails[selectedState].population}
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-903">
                    <p className="text-sm text-gray-500" data-id="element-904">Insured Lives</p>
                    <p className="text-xl font-semibold" data-id="element-905">
                      {stateDetails[selectedState].insuredLives}
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-gray-200" data-id="element-906">
                    <p className="text-sm text-gray-500" data-id="element-907">Coverage Status</p>
                    <div className="mt-1 flex items-center" data-id="element-908">
                      <StatusBadge status={stateData[selectedState]?.status || 'no-policy'} data-id="element-909" />
                    </div>
                  </div>
                </div>
                <div className="mb-6" data-id="element-910">
                  <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-911">
                    Coverage Summary
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg" data-id="element-912">
                    <p className="text-sm text-gray-700" data-id="element-913">
                      {stateDetails[selectedState].summary}
                    </p>
                  </div>
                </div>
                <div className="mb-6" data-id="element-914">
                  <div className="flex items-center justify-between mb-3" data-id="element-915">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-916">
                      Payer Analysis
                    </h3>
                    <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-917">
                      <DownloadIcon className="w-4 h-4 mr-1" data-id="element-918" />
                      Export Data
                    </button>
                  </div>
                  <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-919">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-920">
                      <thead className="bg-gray-50" data-id="element-921">
                        <tr data-id="element-922">
                          <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-923">
                            Payer Name
                          </th>
                          <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-924">
                            Coverage
                          </th>
                          <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-925">
                            Lives
                          </th>
                          <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-926">
                            Prior Auth
                          </th>
                          <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" data-id="element-927">
                            Last Change
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-928">
                        {stateDetails[selectedState].payerData.map((payer, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-929">
                              <td className="px-4 py-3 text-sm text-blue-600 whitespace-nowrap" data-id="element-930">
                                {payer.payer}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap" data-id="element-931">
                                <StatusBadge status={payer.status as any} data-id="element-932" />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-933">
                                {payer.enrollment}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-934">
                                {payer.priorAuth}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-935">
                                {payer.changeDate}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mb-6" data-id="element-936">
                  <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-937">
                    Strategic Recommendations
                  </h3>
                  <div className="space-y-3" data-id="element-938">
                    <div className="p-4 bg-green-50 border border-green-100 rounded-lg" data-id="element-939">
                      <h4 className="text-sm font-medium text-green-800 mb-1" data-id="element-940">
                        Opportunity
                      </h4>
                      <p className="text-sm text-green-700" data-id="element-941">
                        {stateDetails[selectedState].recommendations.opportunity}
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-942">
                      <h4 className="text-sm font-medium text-red-800 mb-1" data-id="element-943">
                        Challenge
                      </h4>
                      <p className="text-sm text-red-700" data-id="element-944">
                        {stateDetails[selectedState].recommendations.challenge}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg" data-id="element-945">
                      <h4 className="text-sm font-medium text-blue-800 mb-1" data-id="element-946">
                        Action Plan
                      </h4>
                      <ul className="pl-5 list-disc text-sm text-blue-700 space-y-1" data-id="element-947">
                        {stateDetails[selectedState].recommendations.actionItems.map((item, idx) => <li key={idx} data-id="element-948">{item}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-6 space-x-3" data-id="element-949">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" onClick={closeStateModal} data-id="element-950">
                    Close
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-951">
                    Generate Full Report
                  </button>
                </div>
              </> :
        // Basic state data or no data available
        <div className="p-8 text-center" data-id="element-952">
                {stateData[selectedState] ? <>
                    <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 inline-block" data-id="element-953">
                      <StatusBadge status={stateData[selectedState].status} data-id="element-954" />
                    </div>
                    <p className="text-gray-600 mb-4" data-id="element-955">
                      {getStateName(selectedState)} has{' '}
                      {stateData[selectedState].status} coverage status.
                      {stateData[selectedState].lives && ` Covers approximately ${stateData[selectedState].lives} lives.`}
                      {stateData[selectedState].dominantPayer && ` Dominant payer: ${stateData[selectedState].dominantPayer}.`}
                    </p>
                    <p className="text-gray-500 mt-4" data-id="element-956">
                      Detailed coverage analysis for{' '}
                      {getStateName(selectedState)} is currently being
                      developed.
                    </p>
                    <p className="text-gray-500 mt-2" data-id="element-957">
                      For comprehensive data, please view TX, CA, NY, or FL.
                    </p>
                  </> : <>
                    <p className="text-gray-600" data-id="element-958">
                      No data is available for {getStateName(selectedState)} at
                      this time.
                    </p>
                    <p className="mt-2 text-gray-600" data-id="element-959">
                      For sample detailed data, please view TX, CA, NY, or FL.
                    </p>
                  </>}
                <div className="mt-6" data-id="element-960">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" onClick={closeStateModal} data-id="element-961">
                    Close
                  </button>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
};
export default EnrollmentMap;